import { useSyncExternalStore } from 'react';
import s from './TableCards.module.css';

// Card rows for wide tables on narrow screens (QA-030, APP-067 §7). Below 720 px a table that opts
// in stacks each row as a card: every value under its column's name, the row's action at the
// bottom at full width. No sideways scrolling, nothing hidden, the action always in reach.
//
// It's the same table: CSS lays it out as cards, and explicit ARIA roles keep it a table for
// screen readers (some browsers drop table semantics once `display` changes). Each value stays
// tied to its column header; the visible label is generated content with empty alternative text,
// so it isn't read twice.

/** Below this width, a table that opts in shows card rows. */
export const CARD_ROWS_BELOW = 720;

const QUERY = `(max-width: ${String(CARD_ROWS_BELOW - 0.02)}px)`;

function mediaQuery(): MediaQueryList | null {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(QUERY)
    : null;
}

function subscribe(onChange: () => void): () => void {
  const query = mediaQuery();
  query?.addEventListener('change', onChange);
  return () => {
    query?.removeEventListener('change', onChange);
  };
}

/** True below 720 px, where opted-in tables show card rows. */
export function useCardRows(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => mediaQuery()?.matches === true,
    () => false,
  );
}

/**
 * Where a cell goes in its card: the heading, the action at the bottom, a labelled value, or the
 * toggle that opens the row's details (APP-109), just above the action.
 */
export type CardPlace = 'title' | 'action' | 'value' | 'toggle';

/**
 * Props for a table's parts in card mode, or nothing in table mode. Spread them on the table's
 * own elements: `<table {...c.table(className)}>`, `<thead {...c.head}>`, `<tbody {...c.body}>`,
 * `<tr {...c.row}>`, `<th {...c.header}>`, `<td {...c.cell('Award')}>`.
 */
export function tableCards(on: boolean) {
  const role = <R extends string>(r: R) => (on ? { role: r } : {});
  return {
    on,
    table: (className?: string) => ({
      className: [className, on ? s.cards : undefined].filter(Boolean).join(' ') || undefined,
      ...role('table'),
    }),
    head: { ...role('rowgroup'), ...(on ? { className: s.head } : {}) },
    body: role('rowgroup'),
    row: role('row'),
    header: role('columnheader'),
    /** A group heading row spanning the table ("Ready to pay · 3"). */
    groupHeader: { ...role('rowheader'), ...(on ? { className: s.group } : {}) },
    cell: (label: string, place: CardPlace = 'value', className?: string) => ({
      ...role('cell'),
      ...(className === undefined ? {} : { className }),
      ...(on ? { 'data-label': label, 'data-card': place } : {}),
    }),
    /** A row that spans every column (an empty state, details): one full-width card. */
    wide: on ? { role: 'cell', 'data-card': 'wide' } : {},
    /** A row's details opened in place (APP-109): a card joined to the row's card above it. */
    details: on ? { role: 'cell', 'data-card': 'details' } : {},
  };
}

export type TableCards = ReturnType<typeof tableCards>;
